import { Component, effect, input, Input, OnInit, SimpleChanges } from "@angular/core";
import { DonorCardService } from "../../../services/donor-card.service";
import { TableModule } from "primeng/table";
import { GenericButtonComponent } from "../../../../global/component/generic/generic-button/generic-button.component";
import { CommonModule } from "@angular/common";
import { DialogModule } from "primeng/dialog";
import { DropdownModule } from "primeng/dropdown";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { MessageService } from "primeng/api";
import { SharedService } from "../../../services/shared.service";
import { GenericConfirmationComponent } from "../../../../global/component/generic/generic-confirmation/generic-confirmation.component";
import { ToggleButtonModule } from "primeng/togglebutton";
import { GenericTextInputComponent } from "../../../../global/component/generic/generic-text-input/generic-text-input.component";

@Component({
    selector: 'app-donor-letters',
    templateUrl: './donor-letters.component.html',
    styleUrls: ['./donor-letters.component.scss'],
    standalone: true,
    imports: [GenericButtonComponent, CommonModule,
        TableModule, DialogModule, DropdownModule,
        ButtonModule, CalendarModule, ToggleButtonModule,
        GenericTextInputComponent,
        ReactiveFormsModule, GenericConfirmationComponent
    ],
    providers: [SharedService, DonorCardService, MessageService]
})
export class DonorLettersComponent {
    @Input() donorId?: number;
    @Input() campaignId?: number;
    
    showAddDonorLetterDialog: boolean = false;
    isEditMode: boolean = false;
    showDeleteDonorLetterDialog: boolean = false;

    donorLetterTypes: any = [];
    donorReturnLetterTypes = [];
    donorLetters = [];
    donorLetterFormGroup: FormGroup;

    donorLetterToDelete = undefined;

    constructor(private formBuilder: FormBuilder,
        private sharedService: SharedService,
        private donorCardService: DonorCardService,
        private messageService: MessageService
    ) {
        this.donorLetterFormGroup = formBuilder.group({
            donorId: [this.donorId, Validators.required],
            letterTypeId: ['', Validators.required],
            letterType: [''],
            dateSent: ['', Validators.required],
            responseReceivedDate: [''],
            letterReturnTypeId: [''],
            employer: [''],
            occupation: [''],
            isRetired: [false]
        })


        effect(() => {
            this.loadDonorLetters();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['donorId'] || changes['campaignId']) {
            this.loadDonorLetters();
        }
    }

    loadDonorLetters(): void {
        if (this.donorId && this.campaignId) {
            this.donorCardService.getDonorCardDonorLetters(this.campaignId, this.donorId).subscribe({
                next: data => {
                    this.donorLetters = data;
                },
                error: err => {
                    console.error('Unable to retrieve donor letters. Please try again later.', err);
                },
            });
        }
    }
    
    loadDonorLetterTypes(): void {
        if (this.donorId) {
            this.donorCardService.getDonorLetterTypes(this.donorId).subscribe({
                next: data => {
                    this.donorLetterTypes = this.donorLetterTypes.concat(data);
                },
                error: err => {
                    console.error('Unable to retrieve donor letters types. Please try again later.', err);
                },
            });
        }
    }
    
    loadDonorReturnLetterTypes(letterTypeId: number): void {
        if (letterTypeId) {
            this.donorCardService.getDonorReturnLetterTypes(letterTypeId).subscribe({
                next: data => {
                    this.donorReturnLetterTypes = data;
                },
                error: err => {
                    console.error('Unable to retrieve donor letters types. Please try again later.', err);
                },
            });
        }
    }
    
    onAddEditDonorLetter(donorLetter?: any) {
        if (!donorLetter) {
            this.isEditMode = false;
            // new fund code
            donorLetter = {
            donorId: this.donorId,
            letterTypeId: '',
            dateSent: '',
            responseReceivedDate: '',
            letterReturnTypeId: '',
            employer: '',
            occupation: '',
            isRetired: false
            };
        }
        else
            this.isEditMode = true;
        
        this.donorLetterTypes = [];
        if (donorLetter.letterTypeId)
        {
            this.donorLetterTypes = [{key: donorLetter.letterTypeId, value: donorLetter.letterType}];
            this.loadDonorReturnLetterTypes(donorLetter.letterTypeId);
        }        
        
        this.loadDonorLetterTypes();

        this.donorLetterFormGroup.reset();
        donorLetter.dateSent = this.sharedService.shortDate(donorLetter.dateSent);
        donorLetter.responseReceivedDate = this.sharedService.shortDate(donorLetter.responseReceivedDate);
        this.donorLetterFormGroup.patchValue(donorLetter);
        this.showAddDonorLetterDialog = true;
        this.enableDisableEmployerInfo(donorLetter.isRetired);
    }

    onDeleteDonorLetter(donorLetter: any) {
        this.donorLetterToDelete = donorLetter;
        this.showDeleteDonorLetterDialog = true;
    }

    onDeleteDonorLetterConfirm(donorLetter: any) {
        this.donorCardService.deleteDonorLetter(donorLetter).subscribe({
            next: (res) => {
                this.loadDonorLetters();
                this.showDeleteDonorLetterDialog = false;
            },
            error: (result) => {
                var errorMessage = 'An error occurred while deleting donor letter.';
                if (result?.error?.message)
                    errorMessage = this.sharedService.decodeHtml(result.error.message);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
            }
        });
    }

    onDeleteDonorLetterCancel() {
        this.showDeleteDonorLetterDialog = false;
    }

    closeAddEditDialog(): void {
        this.showAddDonorLetterDialog = false;
    }

    onIsRetiredChanged(event: any) {
        let emp = event.checked ? "Retired" : "";
        this.donorLetterFormGroup.get('employer')?.setValue(emp);
        this.donorLetterFormGroup.get('occupation')?.setValue(emp);
        this.enableDisableEmployerInfo(event.checked);
    }

    enableDisableEmployerInfo(disable: boolean) {        
        if (disable)
        {
            this.donorLetterFormGroup.get('employer')?.disable();
            this.donorLetterFormGroup.get('occupation')?.disable();
        }
        else
        {
            this.donorLetterFormGroup.get('employer')?.enable();
            this.donorLetterFormGroup.get('occupation')?.enable();
        }
    }

    saveDonorLetter(): void {

        if(this.donorLetterFormGroup.value.letterTypeId === "") {
        this.donorLetterFormGroup.get('letterTypeId')?.markAsTouched();
        }
        if(this.donorLetterFormGroup.value.dateSent === "") {
        this.donorLetterFormGroup.get('dateSent')?.markAsTouched();
        }

        if (this.donorLetterFormGroup.valid) {

            const donorLetter: any = {
                donorId: this.donorId,
                letterTypeId: this.donorLetterFormGroup.value.letterTypeId,
                dateSent: this.donorLetterFormGroup.value.dateSent,
                responseReceivedDate: this.donorLetterFormGroup.value.responseReceivedDate || null,
                letterReturnTypeId: this.donorLetterFormGroup.value.letterReturnTypeId,
                employer: this.donorLetterFormGroup.value.isRetired ? "Retired" : this.donorLetterFormGroup.value.employer,
                occupation: this.donorLetterFormGroup.value.isRetired ? "Retired" : this.donorLetterFormGroup.value.occupation,
                isRetired: this.donorLetterFormGroup.value.isRetired
            };
        
            this.donorCardService.saveDonorLetter(donorLetter).subscribe({
                next: (res) => {
                    this.loadDonorLetters();
                    this.showAddDonorLetterDialog = false;
                },
                error: (result) => {
                    var errorMessage = 'An error occurred while saving donor letter.';
                    if (result?.error?.message)
                        errorMessage = this.sharedService.decodeHtml(result.error.message);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
                }
            });
        }
    }
}